/**
 * Resource cleanup utilities for authentication flow
 * Manages timeouts, intervals, and other disposable resources
 */

import { onUnmounted } from 'vue';
import { createDebugLogger } from './debug';

const logger = createDebugLogger('Cleanup');

/**
 * Resource manager for tracking and cleaning up resources
 */
export class ResourceManager {
  private timeouts = new Set<NodeJS.Timeout>();

  private intervals = new Set<NodeJS.Timeout>();

  private cleanupCallbacks = new Set<() => void>();

  private isCleanedUp = false;

  /**
   * Add a timeout to be tracked
   */
  addTimeout(timeout: NodeJS.Timeout): void {
    if (this.isCleanedUp) {
      clearTimeout(timeout);
      return;
    }
    this.timeouts.add(timeout);
    logger.log(`Added timeout, total: ${this.timeouts.size}`);
  }

  /**
   * Create and track a timeout
   */
  setTimeout(callback: () => void, delay: number): NodeJS.Timeout {
    const timeout = setTimeout(() => {
      callback();
      this.timeouts.delete(timeout);
    }, delay);
    this.addTimeout(timeout);
    return timeout;
  }

  /**
   * Remove a timeout from tracking (when it completes naturally)
   */
  removeTimeout(timeout: NodeJS.Timeout): void {
    if (this.timeouts.delete(timeout)) {
      logger.log(`Removed timeout, remaining: ${this.timeouts.size}`);
    }
  }

  /**
   * Add an interval to be tracked
   */
  addInterval(interval: NodeJS.Timeout): void {
    if (this.isCleanedUp) {
      clearInterval(interval);
      return;
    }
    this.intervals.add(interval);
    logger.log(`Added interval, total: ${this.intervals.size}`);
  }

  /**
   * Create and track an interval
   */
  setInterval(callback: () => void, delay: number): NodeJS.Timeout {
    const interval = setInterval(callback, delay);
    this.addInterval(interval);
    return interval;
  }

  /**
   * Remove an interval from tracking
   */
  removeInterval(interval: NodeJS.Timeout): void {
    if (this.intervals.delete(interval)) {
      clearInterval(interval);
      logger.log(`Removed interval, remaining: ${this.intervals.size}`);
    }
  }

  /**
   * Add a cleanup callback to be called on cleanup
   */
  addCleanupCallback(callback: () => void): void {
    if (this.isCleanedUp) {
      callback();
      return;
    }
    this.cleanupCallbacks.add(callback);
    logger.log(`Added cleanup callback, total: ${this.cleanupCallbacks.size}`);
  }

  /**
   * Remove a cleanup callback
   */
  removeCleanupCallback(callback: () => void): void {
    this.cleanupCallbacks.delete(callback);
  }

  /**
   * Clean up all tracked resources
   */
  cleanup(): void {
    if (this.isCleanedUp) {
      logger.warn('ResourceManager already cleaned up');
      return;
    }

    logger.log(
      `Starting cleanup: ${this.timeouts.size} timeouts, ${this.intervals.size} intervals, ${this.cleanupCallbacks.size} callbacks`
    );

    // Clear all timeouts
    this.timeouts.forEach(timeout => {
      clearTimeout(timeout);
    });
    this.timeouts.clear();

    // Clear all intervals
    this.intervals.forEach(interval => {
      clearInterval(interval);
    });
    this.intervals.clear();

    // Call all cleanup callbacks
    this.cleanupCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        logger.error('Error in cleanup callback:', error);
      }
    });
    this.cleanupCallbacks.clear();

    this.isCleanedUp = true;
    logger.log('Cleanup complete');
  }

  /**
   * Reset the manager (for reuse)
   */
  reset(): void {
    this.cleanup();
    this.isCleanedUp = false;
    logger.log('ResourceManager reset');
  }
}

/**
 * Create a resource manager that automatically cleans up on unmount
 */
export function useResourceManager(): ResourceManager {
  const manager = new ResourceManager();

  // Automatically cleanup on component unmount
  onUnmounted(() => {
    manager.cleanup();
  });

  return manager;
}

/**
 * Global resource manager for auth flow
 */
export const authResourceManager = new ResourceManager();

/**
 * Wrapper to create a managed timeout
 */
export function createManagedTimeout(
  callback: () => void,
  delay: number,
  manager: ResourceManager = authResourceManager
): NodeJS.Timeout {
  return manager.setTimeout(callback, delay);
}

/**
 * Wrapper to create a managed interval
 */
export function createManagedInterval(
  callback: () => void,
  delay: number,
  manager: ResourceManager = authResourceManager
): NodeJS.Timeout {
  return manager.setInterval(callback, delay);
}

/**
 * Register a cleanup callback
 */
export function onCleanup(
  callback: () => void,
  manager: ResourceManager = authResourceManager
): void {
  manager.addCleanupCallback(callback);
}
